import { useEffect, useMemo, useState } from 'react';
import { Edit, Fuel, Loader2, MapPin, Plus, Save, Trash2, X } from 'lucide-react';
import { AdvancedTable } from '../components/AdvancedTable';
import { fuelExceptionPointService } from '../services/fuelExceptionPointService';

type FuelExceptionPoint = {
  id: string;
  name: string;
  pointType: 'fuel_station' | 'warehouse_fueling';
  address: string;
  latitude: number | null;
  longitude: number | null;
  radiusMeters: number;
  status: 'active' | 'inactive';
  notes: string;
};

const emptyForm: FuelExceptionPoint = {
  id: '', name: '', pointType: 'fuel_station', address: '', latitude: null, longitude: null,
  radiusMeters: 250, status: 'active', notes: '',
};

const numberOrNull = (value: string) => {
  const number = Number(value.replace(',', '.'));
  return Number.isFinite(number) ? number : null;
};

const mapRow = (row: any): FuelExceptionPoint => ({
  id: String(row.id),
  name: row.name || '',
  pointType: row.point_type === 'warehouse_fueling' ? 'warehouse_fueling' : 'fuel_station',
  address: row.address || '',
  latitude: row.latitude == null ? null : Number(row.latitude),
  longitude: row.longitude == null ? null : Number(row.longitude),
  radiusMeters: Number(row.radius_meters || 250),
  status: row.status === 'inactive' ? 'inactive' : 'active',
  notes: row.notes || '',
});

export function FuelExceptionPoints() {
  const [points, setPoints] = useState<FuelExceptionPoint[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [form, setForm] = useState<FuelExceptionPoint>(emptyForm);
  const [editing, setEditing] = useState<FuelExceptionPoint | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const response: any = await fuelExceptionPointService.getAll();
      setPoints(Array.isArray(response?.data) ? response.data.map(mapRow) : []);
    } catch (loadError: any) {
      setError(loadError?.message || 'Akaryakit noktalari yuklenemedi');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void load(); }, []);

  const closeForm = () => {
    setFormOpen(false);
    setEditing(null);
    setForm(emptyForm);
  };

  const openForm = (point?: FuelExceptionPoint) => {
    setEditing(point || null);
    setForm(point ? { ...point } : emptyForm);
    setFormOpen(true);
  };

  const save = async () => {
    if (!form.name.trim() || form.latitude == null || form.longitude == null) {
      alert('Nokta adi, enlem ve boylam zorunludur.');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(), pointType: form.pointType, address: form.address.trim() || null,
        latitude: form.latitude, longitude: form.longitude, radiusMeters: form.radiusMeters,
        status: form.status, notes: form.notes.trim() || null,
      };
      if (editing) await fuelExceptionPointService.update(editing.id, payload);
      else await fuelExceptionPointService.create(payload);
      await load();
      closeForm();
    } catch (saveError: any) {
      alert(saveError?.message || 'Akaryakit noktasi kaydedilemedi');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (point: FuelExceptionPoint) => {
    if (!confirm(`${point.name} noktasi silinsin mi?`)) return;
    try {
      await fuelExceptionPointService.delete(point.id);
      await load();
    } catch (deleteError: any) {
      alert(deleteError?.message || 'Akaryakit noktasi silinemedi');
    }
  };

  const tableData = useMemo(() => points.map((point) => ({
    id: point.id,
    name: point.name,
    type: point.pointType === 'warehouse_fueling' ? 'Depo ici dolum' : 'Akaryakit istasyonu',
    coordinates: point.latitude != null && point.longitude != null ? `${point.latitude.toFixed(6)}, ${point.longitude.toFixed(6)}` : '-',
    radius: `${point.radiusMeters} m`,
    status: point.status === 'active' ? 'Aktif' : 'Pasif',
    raw: point,
  })), [points]);

  const columns = useMemo(() => [
    { key: 'name', label: 'Nokta' },
    { key: 'type', label: 'Tur' },
    { key: 'coordinates', label: 'GPS Konumu' },
    { key: 'radius', label: 'Istisna Yaricapi' },
    { key: 'status', label: 'Durum' },
    {
      key: 'id', label: 'Islem', render: (_value: string, row: any) => (
        <div className="flex gap-2">
          <button type="button" onClick={() => openForm(row.raw)} className="rounded-lg border border-blue-200 bg-blue-50 p-2 text-blue-700"><Edit className="h-4 w-4" /></button>
          <button type="button" onClick={() => void remove(row.raw)} className="rounded-lg border border-rose-200 bg-rose-50 p-2 text-rose-700"><Trash2 className="h-4 w-4" /></button>
        </div>
      ),
    },
  ], [points]);

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm md:flex-row md:items-center md:justify-between">
        <div className="flex items-center gap-3">
          <div className="rounded-2xl bg-amber-100 p-3 text-amber-700"><Fuel className="h-6 w-6" /></div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Akaryakit Istisna Noktalari</h1>
            <p className="mt-1 text-sm text-slate-500">Istasyon ve depo ici dolum noktalarinda, duran araclarin yakit seviye dalgalanmalari ihlal sayilmaz.</p>
          </div>
        </div>
        <button type="button" onClick={() => openForm()} className="inline-flex items-center justify-center gap-2 rounded-xl bg-blue-400 px-4 py-2 text-sm font-medium text-slate-950 hover:bg-blue-500"><Plus className="h-4 w-4" />Yeni Nokta</button>
      </div>

      {error && <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">{error}</div>}
      <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
        {loading ? <div className="flex min-h-64 items-center justify-center text-slate-500"><Loader2 className="mr-2 h-5 w-5 animate-spin" />Yukleniyor...</div> : <AdvancedTable data={tableData as any} columns={columns as any} />}
      </div>

      {formOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/25 p-4">
          <div className="w-full max-w-2xl rounded-2xl bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
              <div><h2 className="text-xl font-semibold text-slate-900">{editing ? 'Akaryakit Noktasini Duzenle' : 'Yeni Akaryakit Noktasi'}</h2><p className="mt-1 text-sm text-slate-500">Koordinatlari Google Maps veya tesis GIS kaynagindan girin.</p></div>
              <button type="button" onClick={closeForm} className="rounded-xl p-2 text-slate-500 hover:bg-slate-100"><X className="h-5 w-5" /></button>
            </div>
            <div className="grid gap-4 p-6 md:grid-cols-2">
              <label className="md:col-span-2 text-sm font-medium text-slate-700">Nokta Adi<input value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} className="mt-1 h-11 w-full rounded-xl border border-slate-300 px-3" placeholder="Orn. Aksaray Fabrika Dolum Noktasi" /></label>
              <label className="text-sm font-medium text-slate-700">Nokta Turu<select value={form.pointType} onChange={(event) => setForm({ ...form, pointType: event.target.value === 'warehouse_fueling' ? 'warehouse_fueling' : 'fuel_station' })} className="mt-1 h-11 w-full rounded-xl border border-slate-300 px-3"><option value="fuel_station">Akaryakit istasyonu</option><option value="warehouse_fueling">Depo ici dolum noktasi</option></select></label>
              <label className="text-sm font-medium text-slate-700">Istisna Yaricapi (metre)<input type="number" min="25" max="2000" value={form.radiusMeters} onChange={(event) => setForm({ ...form, radiusMeters: Number(event.target.value) || 250 })} className="mt-1 h-11 w-full rounded-xl border border-slate-300 px-3" /></label>
              <label className="text-sm font-medium text-slate-700">Enlem<input value={form.latitude ?? ''} onChange={(event) => setForm({ ...form, latitude: numberOrNull(event.target.value) })} className="mt-1 h-11 w-full rounded-xl border border-slate-300 px-3" placeholder="38.368694" /></label>
              <label className="text-sm font-medium text-slate-700">Boylam<input value={form.longitude ?? ''} onChange={(event) => setForm({ ...form, longitude: numberOrNull(event.target.value) })} className="mt-1 h-11 w-full rounded-xl border border-slate-300 px-3" placeholder="33.998563" /></label>
              <label className="md:col-span-2 text-sm font-medium text-slate-700">Adres<textarea value={form.address} onChange={(event) => setForm({ ...form, address: event.target.value })} className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2" rows={2} /></label>
              <label className="text-sm font-medium text-slate-700">Durum<select value={form.status} onChange={(event) => setForm({ ...form, status: event.target.value === 'inactive' ? 'inactive' : 'active' })} className="mt-1 h-11 w-full rounded-xl border border-slate-300 px-3"><option value="active">Aktif</option><option value="inactive">Pasif</option></select></label>
              <label className="text-sm font-medium text-slate-700">Notlar<input value={form.notes} onChange={(event) => setForm({ ...form, notes: event.target.value })} className="mt-1 h-11 w-full rounded-xl border border-slate-300 px-3" /></label>
              {form.latitude != null && form.longitude != null && <a className="md:col-span-2 inline-flex items-center gap-2 text-sm font-medium text-blue-700 hover:underline" href={`https://www.google.com/maps?q=${form.latitude},${form.longitude}`} target="_blank" rel="noreferrer"><MapPin className="h-4 w-4" />Konumu haritada ac</a>}
            </div>
            <div className="flex justify-end gap-3 border-t border-slate-200 px-6 py-4"><button type="button" onClick={closeForm} className="rounded-xl border border-slate-300 px-4 py-2 text-sm font-medium">Vazgec</button><button type="button" onClick={() => void save()} disabled={saving} className="inline-flex items-center gap-2 rounded-xl bg-blue-400 px-4 py-2 text-sm font-medium text-slate-950 disabled:opacity-60">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}Kaydet</button></div>
          </div>
        </div>
      )}
    </div>
  );
}
